import React, { useContext } from 'react'
import { Bullet } from 'components/atoms/icons'
import PropTypes from 'prop-types'
import { FeatureFlagsContext } from 'utils/context/FeatureFlagsContext'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { capitalize } from 'utils/capitalize'
import { getFacilityLabels } from 'utils/facilityHelpers'

export const Summary = ({ shipment }) => {
  const {
    carrierPayment,
    coveredBid,
    customerCharges,
    equipment,
    formattedCurrency,
    info,
    status,
    stops = [],
  } = shipment
  const { equipmentDetails, name: equipmentName } = equipment || {}
  const { currentStopIndex, isCovered, isSubmittedToTendered } = status || {}
  const { amount } = coveredBid || {}
  const { pricedUnderContract, totalMiles, totalPalletCount, totalWeight, trackingRequired } =
    info || {}
  const { total } = customerCharges || {}
  const { carrierRate, tcmCarrierPrices } = carrierPayment || {}
  const { cmTarget, listPrice } = tcmCarrierPrices || {}
  const { amount: cmTargetAmount } = cmTarget || {}
  const {
    carrierRateAmount,
    carrierRatePerMile,
    ceilingRateAmount,
    ceilingRatePerMile,
    coveredBidAmount,
    coveredBidPerMile,
    coveredMarginDeltaAmount,
    customerTotalAmount,
    customerTotalPerMile,
    listRateAmount,
    listRatePerMile,
    strategicBuyRateAmount,
    strategicBuyRatePerMile,
    targetMarginRateAmount,
  } = formattedCurrency || {}
  const { facility, info: currentStopInfo } = stops[currentStopIndex] || stops[0] || {}
  const { commodity, genericCommodity } = currentStopInfo || {}
  const { detail } = facility || {}
  const { services = {}, requirements } = detail || {}
  const showContents = commodity || totalWeight > 0 || totalPalletCount > 0
  const showMileage = totalMiles || equipmentName
  let facilityAttributes = trackingRequired ? 'Tracking Required' : ''
  const shipmentRequirements = getFacilityLabels({ ...services, ...requirements })
  facilityAttributes += shipmentRequirements ? ` \u00b7 ${shipmentRequirements}` : ''
  const commodityType = genericCommodity || null

  const { getFlag } = useContext(FeatureFlagsContext)
  const { enabled: maskCmtRates } = getFlag('maskCmtRates')
  const { userPermissions } = useUserDetails()
  const { canViewCmTargetRates } = userPermissions || {}
  const displayCmtRates = !maskCmtRates || canViewCmTargetRates

  const { enabled: displayStrategicBuyRate } = getFlag('displayStrategicBuyRate')

  return (
    <div className='row' data-test='shipment-summary'>
      <div className='col-sm-12 col-md-7 col-lg-6'>
        {showContents && (
          <>
            <div>
              <strong>
                {commodityType ? `${commodityType}: ` : ''}
                {capitalize(commodity)}
              </strong>
              {totalWeight > 0 && (
                <span>
                  <Bullet />
                  {totalWeight} lbs
                </span>
              )}
              {totalPalletCount > 0 && (
                <span>
                  <Bullet />
                  {totalPalletCount} pallets
                </span>
              )}
            </div>
          </>
        )}
        {showMileage && (
          <div>
            <strong>{equipmentName && <span>{equipmentName}</span>}</strong>
            {totalMiles > 0 && (
              <span>
                <Bullet />
                {totalMiles} mi
              </span>
            )}
          </div>
        )}
        {facilityAttributes && (
          <div>
            <strong>{facilityAttributes}</strong>
            {equipmentDetails && (
              <span>
                <Bullet />
                {equipmentDetails}
              </span>
            )}
          </div>
        )}
      </div>
      <div className='col-sm-12 col-md-5 col-lg-6'>
        {amount && isCovered && (
          <p>
            <span className='text-secondary'>Covered</span>&nbsp;
            {coveredBidAmount}
            <span className='text-secondary'> {coveredBidPerMile}</span>
          </p>
        )}
        {carrierRate && !isCovered && (
          <p>
            <span className='text-secondary'>Carrier Rate</span>&nbsp;
            {carrierRateAmount}
            <span className='text-secondary'> {carrierRatePerMile}</span>
          </p>
        )}
        {cmTargetAmount > 0 && displayCmtRates && (
          <p>
            <span className='text-secondary'>CM Target</span>&nbsp;
            {ceilingRateAmount}
            <span className='text-secondary'> {ceilingRatePerMile}</span>
          </p>
        )}
        {displayStrategicBuyRate && strategicBuyRateAmount && (
          <p>
            <span className='text-secondary'>SBR</span>&nbsp;
            {strategicBuyRateAmount}
            <span className='text-secondary'> {strategicBuyRatePerMile}</span>
          </p>
        )}
        {!isSubmittedToTendered && (
          <p>
            <span className='text-secondary'>Cov Marg</span>&nbsp;
            {coveredMarginDeltaAmount}
            <span className='text-secondary'> (Tgt Marg {targetMarginRateAmount})</span>
          </p>
        )}
        {listPrice > 0 && isSubmittedToTendered && (
          <p>
            <span className='text-secondary'>List</span>&nbsp;
            {listRateAmount}
            <span className='text-secondary'> {listRatePerMile}</span>
          </p>
        )}
        {total > 0 && (
          <p>
            <span className='text-secondary'>Ship</span>&nbsp;
            {customerTotalAmount}
            <span className='text-secondary'> {customerTotalPerMile}</span>
            {pricedUnderContract && (
              <span className='text-secondary'>
                <Bullet />
                Contract
              </span>
            )}
          </p>
        )}
        {isSubmittedToTendered && (
          <p>
            <span className='text-secondary'>Tgt Marg</span>&nbsp;
            {targetMarginRateAmount}
          </p>
        )}
      </div>
    </div>
  )
}

Summary.propTypes = {
  shipment: PropTypes.shape({}).isRequired,
}
